import React, { useState, useMemo, useRef } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Badge from '@mui/material/Badge'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Popper from '@mui/material/Popper'
import Grow from '@mui/material/Grow'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuList from '@mui/material/MenuList'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import RateReviewIcon from '@mui/icons-material/RateReview'
import AddCommentIcon from '@mui/icons-material/AddComment'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import UndoIcon from '@mui/icons-material/Undo'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ReplayIcon from '@mui/icons-material/Replay'
import { Link, router } from '@inertiajs/react'
import { TaskFormDialog } from '@/components/tasks/TaskFormDialog'
import { IssueList, Issue } from '@/components/tasks/IssueList'
import { ReviewComposerPanel } from '@/components/tasks/ReviewComposerPanel'
import { AttachmentItem, AttachmentData } from '@/components/tasks/AttachmentItem'
import { AttachmentUploadForm } from '@/components/tasks/AttachmentUploadForm'
import { SubTaskList, SubTask } from '@/components/tasks/SubTaskList'
import { MergeRequestList, MergeRequestData } from '@/components/tasks/MergeRequestList'
import { TaskActivityTimeline } from '@/components/tasks/TaskActivityTimeline'
import { Markdown } from '@/components/Markdown'
import { ReturnToProvider, dataWithReturnTo } from '@/contexts/ReturnToContext'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface ActiveSession {
  id: number
  agent: AgentRef
  started_at: string
  web_url: string
}

interface StatusInfo {
  key: string
  name: string
  color: string
}

export interface TaskDetail {
  id: number
  summary: string
  instructions: string | null
  results: string | null
  plan: string | null
  status: string
  status_color: string
  status_label: string
  position: number
  allowed_transitions: string[]
  assignee: AgentRef | null
  requestor: AgentRef
  reviewer: AgentRef | null
  active_session: ActiveSession | null
  clarifications: Issue[]
  review_items: Issue[]
  attachments: AttachmentData[]
  merge_requests: MergeRequestData[]
  created_at: string
  updated_at: string
}

export interface BoardInfo {
  id: number
  name: string
  slug: string
  is_admin: boolean
  statuses: StatusInfo[]
}

interface BoardRef {
  id: number
  name: string
  slug: string
  agents?: AgentRef[]
}

interface ParentTask {
  id: number
  summary: string
  status: string
  status_color: string
  status_label: string
  board: BoardRef
  web_url: string
}

export interface TaskDetailContentProps {
  tab: string
  board: BoardInfo
  task: TaskDetail
  board_agents: AgentRef[]
  sub_tasks: SubTask[]
  parent_task: ParentTask | null
  accessible_boards: BoardRef[]
  returnTo?: string
  backLink?: { href: string; label: string }
  onTabChange?: (tabSlug: string) => void
}

const TAB_SLUGS = ['results', 'plan', 'instructions', 'clarifications', 'review', 'sub-tasks', 'activity'] as const

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  planning: 'Planning',
  planned: 'Planned',
  in_progress: 'In Progress',
  in_review: 'In Review',
  completed: 'Completed',
  accepted: 'Accepted',
  rejected: 'Rejected',
}

const TRANSITION_ACTIONS: Record<string, Record<string, { label: string; color: string; icon: React.ReactNode }>> = {
  pending: {
    planning: { label: 'Start Planning', color: '#F59E0B', icon: <PlayArrowIcon /> },
    in_progress: { label: 'Start Work', color: '#3B82F6', icon: <PlayArrowIcon /> },
  },
  planning: {
    planned: { label: 'Submit Plan', color: '#D97706', icon: <RateReviewIcon /> },
  },
  planned: {
    in_progress: { label: 'Approve Plan', color: '#3B82F6', icon: <CheckCircleIcon /> },
    planning: { label: 'Request Changes', color: '#F59E0B', icon: <UndoIcon /> },
  },
  in_progress: {
    in_review: { label: 'Submit for Review', color: '#8B5CF6', icon: <RateReviewIcon /> },
  },
  in_review: {
    accepted: { label: 'Accept', color: '#059669', icon: <ThumbUpIcon /> },
    completed: { label: 'Approve', color: '#10B981', icon: <CheckCircleIcon /> },
    in_progress: { label: 'Request Changes', color: '#3B82F6', icon: <UndoIcon /> },
  },
  completed: {
    accepted: { label: 'Accept', color: '#059669', icon: <ThumbUpIcon /> },
    rejected: { label: 'Reject', color: '#EF4444', icon: <ThumbDownIcon /> },
    in_progress: { label: 'Reopen', color: '#3B82F6', icon: <ReplayIcon /> },
  },
  rejected: {
    in_progress: { label: 'Reopen', color: '#3B82F6', icon: <ReplayIcon /> },
  },
}

function SidebarLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="body2"
      fontWeight={600}
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', mb: 1.5 }}
    >
      {children}
    </Typography>
  )
}

function SidebarRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        color={muted ? 'text.secondary' : 'text.primary'}
        sx={{ fontStyle: muted ? 'italic' : 'normal' }}
      >
        {value}
      </Typography>
    </Box>
  )
}

export function TaskDetailContent({
  tab, board, task, board_agents, sub_tasks, parent_task, accessible_boards,
  returnTo, backLink, onTabChange,
}: TaskDetailContentProps) {
  const [showEdit, setShowEdit] = useState(false)
  const [transitioning, setTransitioning] = useState(false)
  const [resolveReviewsDialog, setResolveReviewsDialog] = useState<string | null>(null)
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const statusMenuAnchorRef = useRef<HTMLDivElement>(null)

  const [activeTab, setActiveTab] = useState(() => {
    const idx = TAB_SLUGS.indexOf(tab as typeof TAB_SLUGS[number])
    return idx >= 0 ? idx : 0
  })
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [reviewComposerOpen, setReviewComposerOpen] = useState(false)

  const pendingClarifications = useMemo(
    () => task.clarifications.filter((c) => c.status === 'pending' || c.status === 'draft').length,
    [task.clarifications]
  )

  const pendingReviewItems = useMemo(
    () => task.review_items.filter((r) => r.status === 'pending' || r.status === 'draft').length,
    [task.review_items]
  )

  function startEditing(field: string, currentValue: string | null) {
    setEditingField(field)
    setEditValue(currentValue ?? '')
  }

  function cancelEditing() {
    setEditingField(null)
    setEditValue('')
  }

  function changeTab(index: number) {
    setActiveTab(index)
    cancelEditing()
    if (onTabChange) {
      onTabChange(TAB_SLUGS[index])
    } else {
      const base = `/tasks/boards/${board.slug}/tasks/${task.id}`
      const url = index === 0 ? base : `${base}/${TAB_SLUGS[index]}`
      window.history.pushState({}, '', url)
    }
  }

  function saveField(field: string) {
    setSaving(true)
    router.put(`/tasks/boards/${board.slug}/tasks/${task.id}`, dataWithReturnTo({
      task: { [field]: editValue },
    }, returnTo), {
      onFinish: () => {
        setSaving(false)
        setEditingField(null)
      },
    })
  }

  // Use TRANSITION_ACTIONS key order (not allowed_transitions order) so the
  // primary button is always the positive/forward action (e.g. "Approve Plan"
  // before "Request Changes").
  const allowedSet = new Set(task.allowed_transitions)
  const primaryTransitions = Object.keys(TRANSITION_ACTIONS[task.status] ?? {}).filter(
    (t) => allowedSet.has(t)
  )
  const overflowTransitions = task.allowed_transitions.filter(
    (t) => !TRANSITION_ACTIONS[task.status]?.[t]
  )

  // Build unified action list: primary actions first, then overflow
  const allActions = useMemo(() => {
    const actions: { target: string; label: string; color: string; icon: React.ReactNode }[] = []
    for (const target of primaryTransitions) {
      const action = TRANSITION_ACTIONS[task.status]?.[target]
      if (action) actions.push({ target, ...action })
    }
    for (const target of overflowTransitions) {
      actions.push({
        target,
        label: `Move to ${STATUS_LABELS[target] || target}`,
        color: '#6B7280',
        icon: null,
      })
    }
    return actions
  }, [task.status, task.allowed_transitions])

  // Statuses that should trigger the "resolve unresolved reviews?" dialog
  const APPROVE_ACCEPT_TARGETS = ['completed', 'accepted']

  function handleTransition(targetStatus: string) {
    if (APPROVE_ACCEPT_TARGETS.includes(targetStatus) && pendingReviewItems > 0) {
      setResolveReviewsDialog(targetStatus)
      return
    }
    executeTransition(targetStatus, false)
  }

  function executeTransition(targetStatus: string, resolveReviews: boolean) {
    setTransitioning(true)
    setResolveReviewsDialog(null)
    router.put(`/tasks/boards/${board.slug}/tasks/${task.id}`, dataWithReturnTo({
      task: { status: targetStatus, resolve_reviews: resolveReviews },
    }, returnTo), {
      onFinish: () => setTransitioning(false),
    })
  }

  const taskForDialog = {
    id: task.id,
    summary: task.summary,
    instructions: task.instructions,
    results: task.results,
    plan: task.plan,
    status: task.status,
    status_color: task.status_color,
    status_label: task.status_label,
    position: task.position,
    assignee: task.assignee,
    requestor: task.requestor,
    reviewer: task.reviewer,
    allowed_transitions: task.allowed_transitions,
    parent_id: null,
    active_session: task.active_session ?? null,
    created_at: task.created_at,
    updated_at: task.updated_at,
  }

  function renderEditableField(field: 'instructions' | 'results' | 'plan', label: string) {
    const fieldValue = task[field]
    const isEditing = editingField === field
    const showReviewButton = (field === 'plan' || field === 'results') && fieldValue && !isEditing

    return (
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}
          >
            {label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {showReviewButton && (
              <Button
                size="small"
                startIcon={<AddCommentIcon sx={{ fontSize: 14 }} />}
                onClick={() => setReviewComposerOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  opacity: 0.6,
                  '&:hover': { opacity: 1 },
                  transition: 'opacity 0.15s',
                }}
              >
                Add Review
              </Button>
            )}
            {!isEditing && (
              <IconButton
                size="small"
                onClick={() => startEditing(field, fieldValue)}
                sx={{ opacity: 0.4, '&:hover': { opacity: 1 }, transition: 'opacity 0.15s' }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {isEditing ? (
          <Box>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              disabled={saving}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" onClick={() => saveField(field)} disabled={saving}>
                Save
              </Button>
              <Button size="small" onClick={cancelEditing} disabled={saving}>
                Cancel
              </Button>
            </Box>
          </Box>
        ) : fieldValue ? (
          <Markdown>{fieldValue}</Markdown>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No {label.toLowerCase()} yet.
          </Typography>
        )}
      </Paper>
    )
  }

  return (
    <ReturnToProvider value={returnTo}>
      {/* Header: back link */}
      {backLink && (
        <Box
          component={Link}
          href={backLink.href}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 1.5,
            py: 0.5,
            minHeight: 44,
            textDecoration: 'none',
            '&:hover .back-label': { textDecoration: 'underline' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography
            className="back-label"
            variant="body2"
            color="text.secondary"
          >
            {backLink.label}
          </Typography>
        </Box>
      )}

      {/* Header: title + status badge */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, wordBreak: 'break-word' }}>
          {task.summary}
        </Typography>
        <Chip
          label={task.status_label}
          size="small"
          sx={{
            backgroundColor: task.status_color,
            color: '#fff',
            fontWeight: 600,
            height: 24,
          }}
        />
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left column: main content */}
        <Box sx={{ flex: '1 1 0', minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => changeTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: { xs: 44, md: 36 },
              mb: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '& .MuiTab-root': { minHeight: { xs: 44, md: 36 }, py: 0.5, textTransform: 'none', fontSize: '0.875rem' },
            }}
          >
            <Tab label="Results" />
            <Tab label="Plan" />
            <Tab label="Instructions" />
            <Tab label={
              <Badge badgeContent={pendingClarifications} color="warning" sx={{ pr: 1.5 }}>
                Clarifications
              </Badge>
            } />
            <Tab label={
              <Badge badgeContent={pendingReviewItems} color="warning" sx={{ pr: 1.5 }}>
                Review
              </Badge>
            } />
            <Tab label={
              <Badge badgeContent={sub_tasks.length} color="info" sx={{ pr: 1.5 }}>
                Sub-tasks
              </Badge>
            } />
            <Tab label="Activity" />
          </Tabs>

          {activeTab === 0 && (
            <>
              {renderEditableField('results', 'Results')}
              <MergeRequestList
                mergeRequests={task.merge_requests}
                boardSlug={board.slug}
                taskId={task.id}
              />
            </>
          )}

          {activeTab === 1 && renderEditableField('plan', 'Plan')}

          {activeTab === 2 && renderEditableField('instructions', 'Instructions')}

          {activeTab === 3 && (
            <IssueList
              issues={task.clarifications}
              issueType="clarification"
              boardSlug={board.slug}
              taskId={task.id}
              defaultAssigneeId={task.requestor.id}
              agents={board_agents}
            />
          )}

          {activeTab === 4 && (
            <IssueList
              issues={task.review_items}
              issueType="review"
              boardSlug={board.slug}
              taskId={task.id}
              defaultAssigneeId={task.assignee?.id ?? task.requestor.id}
              agents={board_agents}
              onAddReview={() => setReviewComposerOpen(true)}
            />
          )}

          {activeTab === 5 && (
            <SubTaskList
              subTasks={sub_tasks}
              parentTaskId={task.id}
              currentBoardSlug={board.slug}
              accessibleBoards={accessible_boards}
            />
          )}

          {activeTab === 6 && (
            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <TaskActivityTimeline boardSlug={board.slug} taskId={task.id} />
            </Paper>
          )}
        </Box>

        {/* Right column: sidebar */}
        <Box sx={{
          width: { xs: '100%', md: 320 },
          flexShrink: 0,
          position: { md: 'sticky' },
          top: { md: 24 },
        }}>
          {/* Status Actions */}
          {allActions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {allActions.length === 1 ? (
                // Single action — plain button, no split
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={allActions[0].icon}
                  disabled={transitioning}
                  onClick={() => handleTransition(allActions[0].target)}
                  sx={{
                    backgroundColor: allActions[0].color,
                    '&:hover': { backgroundColor: allActions[0].color, filter: 'brightness(0.9)' },
                    textTransform: 'none',
                  }}
                >
                  {allActions[0].label}
                </Button>
              ) : (
                // Multiple actions — split button
                <>
                  <ButtonGroup
                    variant="contained"
                    ref={statusMenuAnchorRef}
                    fullWidth
                    sx={{
                      '& .MuiButton-root': {
                        backgroundColor: allActions[0].color,
                        '&:hover': { backgroundColor: allActions[0].color, filter: 'brightness(0.9)' },
                        textTransform: 'none',
                      },
                      '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                        borderColor: 'rgba(255,255,255,0.3)',
                      },
                    }}
                  >
                    <Button
                      startIcon={allActions[0].icon}
                      disabled={transitioning}
                      onClick={() => handleTransition(allActions[0].target)}
                    >
                      {allActions[0].label}
                    </Button>
                    <Button
                      size="small"
                      disabled={transitioning}
                      onClick={() => setStatusMenuOpen((prev) => !prev)}
                      sx={{ maxWidth: 40, minWidth: '40px !important' }}
                      aria-label="more status actions"
                    >
                      <ArrowDropDownIcon />
                    </Button>
                  </ButtonGroup>
                  <Popper
                    open={statusMenuOpen}
                    anchorEl={statusMenuAnchorRef.current}
                    placement="bottom-end"
                    transition
                    sx={{ zIndex: 1300 }}
                  >
                    {({ TransitionProps }) => (
                      <Grow {...TransitionProps}>
                        <Paper elevation={4} sx={{ mt: 0.5 }}>
                          <ClickAwayListener onClickAway={() => setStatusMenuOpen(false)}>
                            <MenuList autoFocusItem={statusMenuOpen}>
                              {allActions.slice(1).map((action) => (
                                <MenuItem
                                  key={action.target}
                                  disabled={transitioning}
                                  onClick={() => {
                                    setStatusMenuOpen(false)
                                    handleTransition(action.target)
                                  }}
                                >
                                  {action.icon && (
                                    <ListItemIcon sx={{ color: action.color }}>
                                      {action.icon}
                                    </ListItemIcon>
                                  )}
                                  <ListItemText>{action.label}</ListItemText>
                                </MenuItem>
                              ))}
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                </>
              )}
            </Box>
          )}

          {/* People */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
            <SidebarLabel>People</SidebarLabel>
            <SidebarRow label="Requestor" value={task.requestor.username} />
            {task.assignee ? (
              <SidebarRow label="Assignee" value={task.assignee.username} />
            ) : (
              <SidebarRow label="Assignee" value="Unassigned" muted />
            )}
            {task.reviewer && (
              <SidebarRow label="Reviewer" value={task.reviewer.username} />
            )}
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={() => setShowEdit(true)}
              sx={{ mt: 1.5, textTransform: 'none', fontSize: '0.8125rem', color: 'text.secondary' }}
            >
              Edit task details
            </Button>
          </Paper>

          {/* Active Session */}
          {task.active_session && (
            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
              <SidebarLabel>Active Session</SidebarLabel>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                    flexShrink: 0,
                    '@keyframes activeSessionPulse': {
                      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.5, transform: 'scale(1.3)' },
                    },
                    animation: 'activeSessionPulse 2s ease-in-out infinite',
                  }}
                />
                <Typography
                  component={Link}
                  href={task.active_session.web_url}
                  variant="body2"
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {task.active_session.agent.username}&apos;s session
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Started {new Date(task.active_session.started_at).toLocaleString()}
              </Typography>
            </Paper>
          )}

          {/* Parent Task */}
          {parent_task && (
            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
              <SidebarLabel>Parent Task</SidebarLabel>
              <Box
                component={Link}
                href={parent_task.web_url}
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <Chip
                  label={parent_task.status_label}
                  size="small"
                  sx={{
                    backgroundColor: parent_task.status_color,
                    color: '#fff',
                    height: 18,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" fontWeight={500} noWrap>
                  {parent_task.summary}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {parent_task.board.name}
              </Typography>
            </Paper>
          )}

          {/* Details */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
            <SidebarLabel>Details</SidebarLabel>
            <SidebarRow label="ID" value={`#${task.id}`} />
            <SidebarRow label="Board" value={board.name} />
            <SidebarRow label="Created" value={new Date(task.created_at).toLocaleDateString()} />
            <SidebarRow label="Updated" value={new Date(task.updated_at).toLocaleDateString()} />
          </Paper>

          {/* Attachments */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
            <SidebarLabel>Attachments</SidebarLabel>
            <Box sx={{ mb: 2 }}>
              <AttachmentUploadForm boardSlug={board.slug} taskId={task.id} />
            </Box>
            {task.attachments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No attachments yet.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {task.attachments.map((a) => (
                  <AttachmentItem
                    key={a.id}
                    attachment={a}
                    boardSlug={board.slug}
                    taskId={task.id}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      <TaskFormDialog
        open={showEdit}
        onClose={() => setShowEdit(false)}
        boardSlug={board.slug}
        agents={board_agents}
        task={taskForDialog}
      />

      <ReviewComposerPanel
        open={reviewComposerOpen}
        onClose={() => setReviewComposerOpen(false)}
        boardSlug={board.slug}
        taskId={task.id}
        defaultAssigneeId={task.assignee?.id ?? task.requestor.id}
        agents={board_agents}
      />

      {/* Resolve unresolved reviews dialog */}
      <Dialog
        open={resolveReviewsDialog !== null}
        onClose={() => setResolveReviewsDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unresolved review items</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            This task has {pendingReviewItems} unresolved review {pendingReviewItems === 1 ? 'item' : 'items'}.
            Would you like to automatically resolve {pendingReviewItems === 1 ? 'it' : 'them'}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResolveReviewsDialog(null)}>
            Cancel
          </Button>
          <Button
            onClick={() => executeTransition(resolveReviewsDialog!, false)}
            disabled={transitioning}
          >
            Keep open
          </Button>
          <Button
            variant="contained"
            onClick={() => executeTransition(resolveReviewsDialog!, true)}
            disabled={transitioning}
          >
            Resolve all
          </Button>
        </DialogActions>
      </Dialog>
    </ReturnToProvider>
  )
}
